import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosInstance';
import Swal from 'sweetalert2';

/* ── Company details from environment variables ──────────────────────────── */
const CO = {
  name:    process.env.REACT_APP_CO_NAME             || 'Social Partners',
  phone:   process.env.REACT_APP_CO_PHONE            || '+91 83000 47458',
  phone2:  process.env.REACT_APP_CO_PHONE2           || '+91 80124 38632',
  address: process.env.REACT_APP_CO_ADDRESS          || 'Erode, Tamil Nadu, India',
  udyam:   process.env.REACT_APP_CO_UDYAM            || 'UDYAM-TN-07-0115553',
  website: process.env.REACT_APP_CO_WEBSITE          || 'www.socialpartners.in',
  email:   process.env.REACT_APP_CO_EMAIL            || 'contact@socialpartners.in',
  bank: {
    accountName: process.env.REACT_APP_CO_BANK_ACCOUNT_NAME || 'DHINESH RAAJ P',
    accountNo:   process.env.REACT_APP_CO_BANK_ACCOUNT_NO   || '0023053000013477',
    ifsc:        process.env.REACT_APP_CO_BANK_IFSC          || 'SIBL0000023',
    bankName:    process.env.REACT_APP_CO_BANK_NAME          || 'South Indian Bank',
    gpay:        process.env.REACT_APP_CO_BANK_GPAY          || '8300445569',
  },
};

const DOC_CFG = {
  quotation: { prefix: 'QO',  label: 'QUOTATION', showTerms: true,  showAdvance: false },
  invoice:   { prefix: 'INV', label: 'INVOICE',   showTerms: false, showAdvance: true  },
};

const DEFAULT_TERMS = [
  'All Rates quoted are valid for 10 Days',
  '50 % Payment should be done in advance',
  'Remaining Amount in time of delivery',
  '₹ 8000 Server charges for 12 Months',
  '₹ 20000 Advance',
];

/* ── Helpers ─────────────────────────────────────────────────────────────── */
const fmt = (n) =>
  '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

/* Returns today as YYYY-MM-DD (for <input type="date">) */
const todayISO = () => new Date().toISOString().slice(0, 10);

/* Converts YYYY-MM-DD → "19 May 2026" for the printed document */
const fmtDocDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
};

const blankItem = () => ({ id: Date.now() + Math.random(), description: '', qty: 1, price: '' });

const calcTotals = (items, advance) => {
  const subtotal = items.reduce((s, r) => s + (parseFloat(r.qty) || 0) * (parseFloat(r.price) || 0), 0);
  const adv      = parseFloat(advance) || 0;
  return { subtotal, advance: adv, balance: subtotal - adv };
};

/* ══════════════════════════════════════════════════════════════════════════
   Printable A4 Document
══════════════════════════════════════════════════════════════════════════ */
const PrintDoc = React.forwardRef(function PrintDoc({ doc }, ref) {
  const cfg             = DOC_CFG[doc.type];
  const { subtotal, advance, balance } = calcTotals(doc.items, doc.advance);

  const hdrCell = { padding: '11px 16px', fontSize: 15, fontWeight: 700, color: '#fff', background: '#1a3a8f', textAlign: 'left' };
  const cell    = { padding: '10px 16px', fontSize: 15, borderBottom: '1px solid #e5e7eb', color: '#1f2937' };

  const displayItems = [...doc.items];
  while (displayItems.length < 4) displayItems.push({ id: 'pad' + displayItems.length, description: '', qty: '', price: '', _pad: true });

  return (
    <div ref={ref} style={{
      width: '210mm', height: '297mm', background: '#fff', position: 'relative',
      fontFamily: "'Segoe UI', Arial, sans-serif", boxSizing: 'border-box',
      border: 'none', outline: 'none', overflow: 'hidden',
    }}>

      {/* ── Blue gradient header ── */}
      {/* No overflow:hidden — lets the wave SVG extend 4px past the div edge */}
      <div style={{
        background: 'linear-gradient(135deg, #0a1f5c 0%, #1a3a9e 55%, #2563eb 100%)',
        padding: '30px 50px 60px', position: 'relative', zIndex: 2,
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,.05)', overflow: 'hidden' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
          <div style={{ color: '#fff', fontSize: 56, fontWeight: 900, letterSpacing: -1, lineHeight: 1 }}>
            {cfg.label}
          </div>
          <div style={{ color: '#fff', textAlign: 'right' }}>
            <div style={{ fontSize: 12, opacity: .75, textTransform: 'uppercase', letterSpacing: 1.4 }}>Document No.</div>
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 3 }}>{doc.number}</div>
          </div>
        </div>

        {/* Wave extends 4px below the header div — eliminates any div-boundary line */}
        <svg viewBox="0 0 794 60" style={{ position: 'absolute', bottom: -4, left: 0, width: '100%', display: 'block' }}>
          <path d="M0,60 L0,35 Q200,0 397,32 Q594,60 794,22 L794,60 Z" fill="#fff" />
        </svg>
      </div>

      {/* ── Body — slides 4px under the header so the wave covers the seam ── */}
      <div style={{ padding: '0 50px 90px', position: 'relative', zIndex: 1, marginTop: -4, background: '#fff', border: 'none', outline: 'none' }}>

        {/* Bill To / From */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18, marginTop: 20, border: 'none' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 19, fontWeight: 800, color: '#0a1f5c', marginBottom: 6 }}>Bill To:</div>
            <div style={{ fontSize: 15, color: '#374151', lineHeight: 1.75 }}>
              <div style={{ fontWeight: 700 }}>{doc.customer.name || '—'}</div>
              {doc.customer.phone   && <div>{doc.customer.phone}</div>}
              {doc.customer.address && <div style={{ whiteSpace: 'pre-line' }}>{doc.customer.address}</div>}
            </div>
            <div style={{ marginTop: 8, fontSize: 15, color: '#374151' }}>
              <b>Date:</b> {fmtDocDate(doc.date)}
            </div>
          </div>
          <div style={{ flex: 1, textAlign: 'right' }}>
            <div style={{ fontSize: 19, fontWeight: 800, color: '#0a1f5c', marginBottom: 6 }}>From:</div>
            <div style={{ fontSize: 15, color: '#374151', lineHeight: 1.75 }}>
              <div style={{ fontWeight: 700 }}>{CO.name}</div>
              <div>{CO.phone}</div>
              <div>{CO.address}</div>
              <div>{CO.udyam}</div>
            </div>
          </div>
        </div>

        {/* Items table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden' }}>
          <thead>
            <tr>
              <th style={{ ...hdrCell, width: '55%', borderRadius: '8px 0 0 0' }}>Description</th>
              <th style={{ ...hdrCell, textAlign: 'center', width: '12%' }}>Qty</th>
              <th style={{ ...hdrCell, textAlign: 'right', width: '16%' }}>Price</th>
              <th style={{ ...hdrCell, textAlign: 'right', width: '17%', borderRadius: '0 8px 0 0' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {displayItems.map((item, i) => {
              const rowTotal = (parseFloat(item.qty) || 0) * (parseFloat(item.price) || 0);
              return (
                <tr key={item.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                  <td style={cell}>{item._pad ? '' : (item.description || '—')}</td>
                  <td style={{ ...cell, textAlign: 'center' }}>{item._pad ? '' : item.qty}</td>
                  <td style={{ ...cell, textAlign: 'right' }}>{item._pad ? '' : (item.price ? fmt(item.price) : '—')}</td>
                  <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>
                    {item._pad ? '' : (rowTotal ? fmt(rowTotal) : '—')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
          <div style={{ width: 290 }}>
            {cfg.showAdvance && advance > 0 ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 16px', background: '#f3f4f6', fontSize: 14, color: '#374151', borderRadius: '8px 8px 0 0', border: '1px solid #e5e7eb' }}>
                  <span style={{ fontWeight: 600 }}>Total</span>
                  <span style={{ fontWeight: 700 }}>{fmt(subtotal)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 16px', background: '#fef9c3', fontSize: 14, color: '#374151', border: '1px solid #e5e7eb', borderTop: 'none' }}>
                  <span style={{ fontWeight: 600 }}>Advance Paid (-)</span>
                  <span style={{ fontWeight: 700, color: '#d97706' }}>{fmt(advance)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px', background: '#1a3a8f', borderRadius: '0 0 8px 8px', fontSize: 15 }}>
                  <span style={{ fontWeight: 700, color: '#fff' }}>Balance</span>
                  <span style={{ fontWeight: 800, color: '#fff' }}>{fmt(balance)}</span>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px', background: '#1a3a8f', borderRadius: 8, fontSize: 15 }}>
                <span style={{ fontWeight: 700, color: '#fff' }}>Sub Total</span>
                <span style={{ fontWeight: 800, color: '#fff' }}>{fmt(subtotal)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Terms + Bank */}
        <div style={{ display: 'flex', gap: 30, marginBottom: 16 }}>
          {cfg.showTerms && doc.terms.length > 0 && (
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#0a1f5c', marginBottom: 8 }}>Terms &amp; Conditions:</div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {doc.terms.filter(Boolean).map((t, i) => (
                  <li key={i} style={{ fontSize: 14, color: '#374151', marginBottom: 5, display: 'flex', gap: 6 }}>
                    <span style={{ color: '#1a3a8f', fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>◆</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div style={{ flex: 1, maxWidth: cfg.showTerms ? undefined : 300 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0a1f5c', marginBottom: 8 }}>Bank Account Details</div>
            <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.85 }}>
              <div><b>Account Name:</b> {CO.bank.accountName}</div>
              <div><b>Account Number:</b> {CO.bank.accountNo}</div>
              <div><b>IFSC:</b> {CO.bank.ifsc}</div>
              <div><b>Bank Name:</b> {CO.bank.bankName}</div>
              <div><b>GPAY:</b> {CO.bank.gpay}</div>
            </div>
          </div>
        </div>

        {/* Thank You */}
        <div style={{ textAlign: 'center', padding: '6px 0 10px' }}>
          <span style={{ fontSize: 30, fontWeight: 700, color: '#1a3a8f', fontStyle: 'italic', letterSpacing: 1 }}>
            Thank You!
          </span>
        </div>
      </div>

      {/* ── Footer bar ── */}
      <div style={{
        background: '#0a1f5c', color: '#fff', fontSize: 13,
        display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 32,
        padding: '14px 50px',
        position: 'absolute', bottom: 0, left: 0, right: 0,
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <i className="fas fa-globe" style={{ fontSize: 13 }}></i>
          {CO.website}
        </span>
        <span style={{ opacity: .35, fontSize: 18 }}>|</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <i className="fas fa-envelope" style={{ fontSize: 13 }}></i>
          {CO.email}
        </span>
        <span style={{ opacity: .35, fontSize: 18 }}>|</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <i className="fas fa-phone-alt" style={{ fontSize: 13 }}></i>
          {CO.phone} &nbsp;|&nbsp; {CO.phone2}
        </span>
      </div>
    </div>
  );
});

/* ══════════════════════════════════════════════════════════════════════════
   Main Page
══════════════════════════════════════════════════════════════════════════ */
const MAIN_API = process.env.REACT_APP_API_URL || 'https://erpapi.socialpartners.in/api';

const emptyDoc = (type, number) => ({
  type,
  number:       number || '',
  date:         todayISO(),
  customer:     { name: '', phone: '', address: '' },
  items:        [blankItem()],
  advance:      '',
  terms:        [...DEFAULT_TERMS],
  lead_id:      null,
  account_id:   null,
  revision:     0,
  revision_log: [],
});

export default function QuotationPage() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const [docType,      setDocType]      = useState('quotation');
  const [doc,          setDoc]          = useState(emptyDoc('quotation'));
  const [numLoading,   setNumLoading]   = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [saved,        setSaved]        = useState(false);
  const [history,      setHistory]      = useState([]);
  const [histTab,      setHistTab]      = useState('quotation');
  const [scale,        setScale]        = useState(0.65);
  const [accounts,     setAccounts]     = useState([]);
  const [leads,        setLeads]        = useState([]);
  const [srcLoading,   setSrcLoading]   = useState(true);
  const [accSearch,    setAccSearch]    = useState('');
  const [showAccDrop,  setShowAccDrop]  = useState(false);
  const [accFetching,  setAccFetching]  = useState(false);
  const [advancePct,      setAdvancePct]      = useState(50);
  const [sourceQuotations, setSourceQuotations] = useState([]);
  const [sourceQuotId,     setSourceQuotId]     = useState('');
  const [sourceQuotLoad,   setSourceQuotLoad]   = useState(false);
  const [showRevHistory,   setShowRevHistory]   = useState(false);
  const accDropRef                      = useRef();
  const printRef                        = useRef();

  /* ── Load next number from API ── */
  const loadNextNumber = useCallback(async (type) => {
    setNumLoading(true);
    try {
      const res = await axiosInstance.get(`/quotations/next-number/${type}`);
      setDoc(d => ({ ...d, number: res.data.nextNumber }));
    } catch {
      /* fallback: generate locally */
      const d    = new Date();
      const ym   = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
      const pfx  = DOC_CFG[type].prefix;
      setDoc(d => ({ ...d, number: `${pfx}-${ym}-1` }));
    } finally {
      setNumLoading(false);
    }
  }, []);

  /* ── Load history ── */
  const loadHistory = useCallback(async (type) => {
    try {
      const res = await axiosInstance.get(`/quotations?type=${type}`);
      setHistory(res.data.data || []);
    } catch {
      setHistory([]);
    }
  }, []);

  useEffect(() => { loadNextNumber('quotation'); loadHistory('quotation'); }, [loadNextNumber, loadHistory]);


  /* ── Load accounts and leads for dropdowns ── */
  useEffect(() => {
    setSrcLoading(true);
    let accountsDone = false;
    let leadsDone = false;
    const checkDone = () => { if (accountsDone && leadsDone) setSrcLoading(false); };

    axiosInstance.get('/accounts')
      .then(r => { const list = r.data.data || r.data || []; setAccounts(Array.isArray(list) ? list : []); })
      .catch(() => setAccounts([]))
      .finally(() => { accountsDone = true; checkDone(); });

    axiosInstance.get('/leads')
      .then(r => { const list = r.data.data || r.data || []; setLeads(Array.isArray(list) ? list : []); })
      .catch(err => {
        const status = err?.response?.status;
        if (status !== 404) console.error('Leads fetch error:', err.message);
        setLeads([]);
      })
      .finally(() => { leadsDone = true; checkDone(); });
  }, []);

  /* ── Close dropdown when clicking outside ── */
  useEffect(() => {
    const handler = (e) => {
      if (accDropRef.current && !accDropRef.current.contains(e.target))
        setShowAccDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ── Select lead or account → fetch details → fill form ── */
  const selectSource = async (item, isLead) => {
    setAccSearch(item.company_name || '');
    setShowAccDrop(false);
    setAccFetching(true);
    try {
      let name = item.company_name || '';
      let phone = item.phone || '';
      let addr = '';

      if (isLead) {
        const r = await axiosInstance.get(`/leads/details/${item.id}`);
        const d = r.data.data;
        name  = d.company_name || '';
        phone = d.phone || '';
        addr  = [
          d.address_line1,
          d.address_line2,
          d.city?.name,
          d.state?.name,
          d.country?.name,
        ].filter(Boolean).join(', ');
      } else {
        const r = await axiosInstance.get(`/accounts/details/${item.id}`);
        const d = r.data.data;
        name  = d.company_name || '';
        phone = d.phone || '';
        addr  = [
          d.billing_address_line1,
          d.billing_address_line2,
          d.billing_city?.name,
          d.billing_state?.name,
          d.billing_country?.name,
          d.billing_zip,
        ].filter(Boolean).join(', ');
      }

      setDoc(prev => ({
        ...prev,
        customer:   { name, phone, address: addr },
        lead_id:    isLead ? item.id : null,
        account_id: isLead ? null : item.id,
      }));
    } catch {
      setDoc(prev => ({
        ...prev,
        customer:   { name: item.company_name || '', phone: item.phone || '', address: '' },
        lead_id:    isLead ? item.id : null,
        account_id: isLead ? null : item.id,
      }));
    } finally {
      setAccFetching(false);
    }

    // For proforma / invoice: fetch the account's quotations so user can pick one as the base
    if (!isLead) {
      setSourceQuotId('');
      setSourceQuotations([]);
      setSourceQuotLoad(true);
      axiosInstance.get(`/quotations?account_id=${item.id}&type=quotation`)
        .then(r => setSourceQuotations(r.data.data || []))
        .catch(() => setSourceQuotations([]))
        .finally(() => setSourceQuotLoad(false));
    }
  };

  /* ── Advance % → update matching terms lines automatically ── */
  const applyAdvancePct = (pct) => {
    const pctNum = parseFloat(pct) || 0;
    // Use current subtotal at call time via functional updater
    setDoc(d => {
      const sub    = d.items.reduce((s, r) => s + (parseFloat(r.qty) || 0) * (parseFloat(r.price) || 0), 0);
      const amount = Math.round((pctNum / 100) * sub);
      const amtStr = amount.toLocaleString('en-IN');
      return {
        ...d,
        terms: d.terms.map(t => {
          if (/\d+\s*%\s*Payment should be done in advance/i.test(t))
            return `${pctNum} % Payment should be done in advance`;
          if (/₹\s*[\d,]+\s*Advance/i.test(t))
            return `₹ ${amtStr} Advance`;
          return t;
        }),
      };
    });
    setAdvancePct(pct);
  };

  /* ── Pick a quotation as the base for proforma/invoice — copies items + locks them ── */
  const applySourceQuot = (quotId) => {
    setSourceQuotId(quotId);
    if (!quotId) return;
    const quot = sourceQuotations.find(q => String(q.id) === String(quotId));
    if (!quot) return;
    const items = (quot.items || []).map(i => ({ ...i, id: i.id || Date.now() + Math.random() }));
    setDoc(prev => ({ ...prev, items }));
  };

  /* ── Switch document type ── */
  const switchType = async (type) => {
    setDocType(type);
    setHistTab(type);
    setSaved(false);
    setAccSearch('');
    setShowAccDrop(false);
    setAdvancePct(50);
    setSourceQuotId('');
    setSourceQuotations([]);
    setDoc(emptyDoc(type));
    await loadNextNumber(type);
    await loadHistory(type);
  };

  /* ── Load a saved doc from history (or from navigation state) ── */
  const loadDoc = (saved) => {
    const type = saved.type || 'quotation';
    setDocType(type);
    setHistTab(type);
    setDoc({
      type,
      number:       saved.number,
      date:         saved.date_str || todayISO(),
      customer: {
        name:    saved.customer_name    || '',
        phone:   saved.customer_phone   || '',
        address: saved.customer_address || '',
      },
      items:        (saved.items || [blankItem()]).map(i => ({ ...i, id: i.id || Date.now() + Math.random() })),
      advance:      saved.advance || '',
      terms:        saved.terms   || [...DEFAULT_TERMS],
      lead_id:      saved.lead_id      || null,
      account_id:   saved.account_id   || null,
      revision:     saved.revision     || 0,
      revision_log: saved.revision_log || [],
    });
    setAccSearch(saved.customer_name || '');
    setShowRevHistory(false);
    setSaved(true);
  };

  /* ── Load doc from navigation state (View from lead/account details, or Edit from Documents list) ── */
  useEffect(() => {
    const navDoc = location.state?.editDoc || location.state?.viewDoc;
    if (navDoc) {
      loadDoc(navDoc);
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── New document ── */
  const newDoc = async () => {
    setSaved(false);
    setAccSearch('');
    setShowAccDrop(false);
    setAdvancePct(50);
    setSourceQuotId('');
    setSourceQuotations([]);
    setDoc(emptyDoc(docType));
    await loadNextNumber(docType);
  };

  /* ── Save to DB ── */
  const saveToDb = async (silent = false) => {
    // Capture BEFORE any state change — true = editing an existing doc
    const isEditing = saved;
    setSaving(true);
    try {
      const { subtotal, advance, balance } = calcTotals(doc.items, doc.advance);
      const parts = doc.number.split('-');
      const ym    = parts.length >= 2 ? parts[1] : (() => { const n = new Date(); return `${n.getFullYear()}${String(n.getMonth()+1).padStart(2,'0')}`; })();
      const seq   = parseInt(parts[parts.length - 1]) || 1;

      await axiosInstance.post('/quotations', {
        type:             docType,
        number:           doc.number,
        year_month:       ym,
        sequence:         seq,
        customer_name:    doc.customer.name,
        customer_phone:   doc.customer.phone,
        customer_address: doc.customer.address,
        date_str:         doc.date,
        items:            doc.items,
        advance,
        subtotal,
        balance,
        terms:            doc.terms,
        lead_id:          doc.lead_id    || null,
        account_id:       doc.account_id || null,
      });

      const savedNumber = doc.number;
      await loadHistory(docType);

      if (!silent) {
        Swal.fire({
          toast: true, position: 'top-end', icon: 'success',
          title: isEditing ? `${DOC_CFG[docType].label} updated!` : `${DOC_CFG[docType].label} saved!`,
          text: savedNumber,
          showConfirmButton: false, timer: 2500, timerProgressBar: true,
        });

        if (isEditing) {
          // Editing existing doc — stay on the same form so user can verify
          setSaved(true);
        } else {
          // New document — clear form and load next number
          setSaved(false);
          setAccSearch('');
          setDoc(emptyDoc(docType));
          await loadNextNumber(docType);
        }
      } else {
        setSaved(true);
      }
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Unknown error';
      if (!silent) {
        Swal.fire({
          icon: 'error', title: 'Save Failed',
          text: msg, confirmButtonColor: '#2563eb',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  /* ── Print ── */
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: doc.number,
    pageStyle: `
      @page {
        size: A4 portrait;
        margin: 0;
      }
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: 210mm !important;
        height: 297mm !important;
        overflow: hidden !important;
        background: white !important;
      }
      /* Force the print container to fill the entire A4 page box */
      body > div:first-child {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 210mm !important;
        height: 297mm !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden !important;
      }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
    `,
  });

  /* ── Field helpers ── */
  const set        = (field, val)        => setDoc(d => ({ ...d, [field]: val }));
  const setCust    = (field, val)        => setDoc(d => ({ ...d, customer: { ...d.customer, [field]: val } }));
  const setItem    = (id, field, val)    => setDoc(d => ({ ...d, items: d.items.map(r => r.id === id ? { ...r, [field]: val } : r) }));
  const addItem    = ()                  => setDoc(d => ({ ...d, items: [...d.items, blankItem()] }));
  const removeItem = (id)               => setDoc(d => ({ ...d, items: d.items.filter(r => r.id !== id) }));
  const setTerm    = (i, val)           => setDoc(d => { const t = [...d.terms]; t[i] = val; return { ...d, terms: t }; });
  const addTerm    = ()                 => setDoc(d => ({ ...d, terms: [...d.terms, ''] }));
  const removeTerm = (i)               => setDoc(d => ({ ...d, terms: d.terms.filter((_, j) => j !== i) }));

  const cfg            = DOC_CFG[docType];
  const { subtotal, advance, balance } = calcTotals(doc.items, doc.advance);

  /* ── UI styles ── */
  const lbl    = { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 4, display: 'block' };
  const inp    = { width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 11px', fontSize: 13, outline: 'none', background: '#fff', boxSizing: 'border-box', color: '#1f2937' };
  const card   = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 18, marginBottom: 14, boxShadow: '0 1px 3px rgba(0,0,0,.04)' };
  const typeBtn = (t) => ({
    flex: 1, padding: '9px 4px', fontSize: 12, fontWeight: 700,
    borderRadius: 8, cursor: 'pointer', border: 'none',
    background: docType === t ? 'linear-gradient(135deg,#0a1f5c,#2563eb)' : '#f1f5f9',
    color: docType === t ? '#fff' : '#64748b', transition: 'all .2s',
  });

  return (
    <>
    <div className="page-wrapper" style={{ background: '#f1f5f9' }}>
      <div className="content container-fluid" style={{ padding: '16px 20px' }}>

        {/* ── Page header ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 12, background: 'linear-gradient(135deg,#0a1f5c,#2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-file-invoice" style={{ color: '#fff', fontSize: 18 }}></i>
            </div>
            <div>
              <h5 style={{ margin: 0, fontWeight: 800, color: '#0f172a', fontSize: 20 }}>
                {saved ? `Edit ${DOC_CFG[docType].label}` : `Create ${DOC_CFG[docType].label}`}
              </h5>
              <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>Quotation · Invoice</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <label style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
              Zoom
              <input type="range" min="0.4" max="1" step="0.05" value={scale}
                onChange={e => setScale(parseFloat(e.target.value))}
                style={{ width: 72 }} />
            </label>
            {saved && doc.revision > 0 && (
              <button onClick={() => setShowRevHistory(true)}
                style={{ background: '#fef9c3', color: '#92400e', border: '1px solid #fde68a', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <i className="fas fa-history me-1"></i>Revisions ({doc.revision})
              </button>
            )}
            <button onClick={() => saveToDb(false)} disabled={saving}
              style={{ background: '#fff', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {saving
                ? <><i className="fas fa-spinner fa-spin me-1"></i>Saving…</>
                : <><i className="fas fa-save me-1"></i>{saved ? 'Update' : 'Save'}</>}
            </button>
            <button onClick={handlePrint}
              style={{ background: 'linear-gradient(135deg,#0a1f5c,#2563eb)', color: '#fff', border: 'none', borderRadius: 10, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <i className="fas fa-print me-1"></i>Print / PDF
            </button>
            <button onClick={newDoc}
              style={{ background: '#fff', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <i className="fas fa-plus me-1"></i>New
            </button>
            <button onClick={() => navigate('/documents')}
              style={{ background: '#0a1f5c', color: '#fff', border: 'none', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <i className="fas fa-list me-1"></i>Documents
            </button>
          </div>
        </div>

        {/* ══════════════ CREATE VIEW ══════════════ */}
        <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>

          {/* ══ LEFT: Form ══ */}
          <div style={{ width: 360, flexShrink: 0, maxHeight: 'calc(100vh - 130px)', overflowY: 'auto', paddingRight: 4 }}>

            {/* Type switcher */}
            <div style={card}>
              <div style={{ display: 'flex', gap: 8 }}>
                {Object.keys(DOC_CFG).map(t => (
                  <button key={t} onClick={() => switchType(t)} style={typeBtn(t)}>
                    {DOC_CFG[t].label}
                  </button>
                ))}
              </div>
            </div>

            {/* Document number + date */}
            <div style={card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className="fas fa-hashtag" style={{ color: '#2563eb' }}></i> Document Info
              </div>
              <div style={{ marginBottom: 10 }}>
                <span style={lbl}>Document Number {numLoading && <span style={{ color: '#94a3b8' }}>· loading…</span>}</span>
                <input style={inp} value={doc.number}
                  onChange={e => set('number', e.target.value)} />
                <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 3, display: 'block' }}>
                  Auto-generated from DB · you can edit if needed
                </span>
              </div>
              <div>
                <span style={lbl}>Date</span>
                <input
                  type="date"
                  style={inp}
                  value={doc.date}
                  onChange={e => set('date', e.target.value)}
                />
                <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 3, display: 'block' }}>
                  Prints as: {fmtDocDate(doc.date)}
                </span>
              </div>
            </div>

            {/* Customer */}
            <div style={card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span><i className="fas fa-building" style={{ color: '#2563eb' }}></i> Customer Details</span>
                {doc.customer.name && (
                  <button onClick={() => { setAccSearch(''); setDoc(d => ({ ...d, customer: { name: '', phone: '', address: '' }, lead_id: null, account_id: null })); }}
                    style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 12 }}>
                    <i className="fas fa-times me-1"></i>Clear
                  </button>
                )}
              </div>

              {/* Linked entity badge */}
              {(doc.lead_id || doc.account_id) ? (
                <div style={{ marginBottom: 10, padding: '6px 10px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                  <i className={`fas ${doc.lead_id ? 'fa-user-tie' : 'fa-building'} text-success`}></i>
                  <span style={{ color: '#166534', fontWeight: 600 }}>
                    Linked to {doc.lead_id ? 'Lead' : 'Account'} #{doc.lead_id || doc.account_id}
                  </span>
                  <span style={{ color: '#16a34a' }}>— will appear in {doc.lead_id ? 'Lead' : 'Account'} Details</span>
                </div>
              ) : (
                <div style={{ marginBottom: 10, padding: '6px 10px', background: '#fefce8', border: '1px solid #fde047', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                  <i className="fas fa-exclamation-triangle" style={{ color: '#ca8a04' }}></i>
                  <span style={{ color: '#92400e' }}>Not linked — select from the dropdown below to link to a {docType === 'quotation' ? 'Lead' : 'Account'}</span>
                </div>
              )}


              {/* Searchable dropdown — Leads for Quotation, Accounts for Proforma/Invoice */}
              {(() => {
                const isLead   = docType === 'quotation';
                const srcList  = isLead ? leads : accounts;
                const label    = isLead ? 'Search & Select Lead' : 'Search & Select Account';
                const emptyMsg = isLead ? 'No leads found' : 'No accounts found';

                const filtered = srcList.filter(a => {
                  const q = accSearch.toLowerCase();
                  return !q
                    || (a.company_name || '').toLowerCase().includes(q)
                    || (a.phone || '').includes(q);
                }).slice(0, 30);

                return (
                  <div style={{ marginBottom: 12 }} ref={accDropRef}>
                    <span style={lbl}>{label}</span>
                    <div style={{ position: 'relative' }}>
                      <i className="fas fa-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 12, zIndex: 1 }}></i>
                      <input
                        style={{ ...inp, paddingLeft: 30, paddingRight: accFetching ? 30 : 10 }}
                        placeholder={`Type company name or phone…`}
                        value={accSearch}
                        onChange={e => { setAccSearch(e.target.value); setShowAccDrop(true); }}
                        onFocus={() => setShowAccDrop(true)}
                      />
                      {accFetching && (
                        <i className="fas fa-spinner fa-spin" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 12 }}></i>
                      )}

                      {showAccDrop && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
                          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
                          boxShadow: '0 8px 24px rgba(0,0,0,.12)', maxHeight: 240, overflowY: 'auto',
                          marginTop: 4,
                        }}>
                          {srcLoading ? (
                            <div style={{ padding: '16px 14px', color: '#94a3b8', fontSize: 13, textAlign: 'center' }}>
                              <i className="fas fa-spinner fa-spin me-1"></i>Loading…
                            </div>
                          ) : filtered.length === 0 ? (
                            <div style={{ padding: '16px 14px', color: '#94a3b8', fontSize: 13, textAlign: 'center' }}>
                              {emptyMsg}
                            </div>
                          ) : filtered.map(item => (
                            <div key={item.id}
                              onMouseDown={() => selectSource(item, isLead)}
                              style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                              onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                            >
                              <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <i className={`fas ${isLead ? 'fa-user-tie' : 'fa-building'}`} style={{ fontSize: 11, color: '#2563eb' }}></i>
                                {item.company_name || '—'}
                              </div>
                              {item.phone && (
                                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                  <i className="fas fa-phone" style={{ fontSize: 10, marginRight: 4 }}></i>
                                  {item.phone}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Auto-filled fields (still editable) */}
              <div style={{ marginBottom: 10 }}>
                <span style={lbl}>Name</span>
                <input style={inp} value={doc.customer.name} placeholder="Company / customer name"
                  onChange={e => setCust('name', e.target.value)} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <span style={lbl}>Phone</span>
                <input style={inp} value={doc.customer.phone} placeholder="+91 XXXXX XXXXX"
                  onChange={e => setCust('phone', e.target.value)} />
              </div>
              <div>
                <span style={lbl}>Address</span>
                <textarea style={{ ...inp, resize: 'vertical', minHeight: 60 }}
                  value={doc.customer.address} placeholder="Billing address"
                  onChange={e => setCust('address', e.target.value)} />
              </div>
            </div>

            {/* Base Quotation picker — proforma / invoice only */}
            {cfg.showAdvance && (
              <div style={{ ...card, border: '1px solid #d1fae5', background: '#f0fdf4' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#065f46', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <i className="fas fa-file-alt" style={{ color: '#16a34a' }}></i> Base Quotation
                  <span style={{ fontSize: 11, fontWeight: 400, color: '#6b7280', marginLeft: 4 }}>
                    — pick a quotation to pre-fill items
                  </span>
                </div>

                {!doc.account_id ? (
                  <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>
                    <i className="fas fa-arrow-up me-1"></i>Select an account above first
                  </p>
                ) : sourceQuotLoad ? (
                  <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>
                    <i className="fas fa-spinner fa-spin me-1"></i>Loading quotations…
                  </p>
                ) : sourceQuotations.length === 0 ? (
                  <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>
                    No quotations found for this account — items will be blank
                  </p>
                ) : (
                  <>
                    <span style={lbl}>Select Quotation</span>
                    <select
                      style={{ ...inp, cursor: 'pointer' }}
                      value={sourceQuotId}
                      onChange={e => applySourceQuot(e.target.value)}
                    >
                      <option value="">— choose a quotation —</option>
                      {sourceQuotations.map(q => (
                        <option key={q.id} value={q.id}>
                          {q.number}  ·  {q.customer_name}  ·  {fmt(q.subtotal)}
                        </option>
                      ))}
                    </select>
                    {sourceQuotId && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#059669', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i className="fas fa-lock"></i>
                        Items are locked from this quotation. Only the advance amount and address are editable.
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Line items — locked (read-only) when a source quotation is selected */}
            {cfg.showAdvance && sourceQuotId ? (
              /* ── READ-ONLY items from source quotation ── */
              <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className="fas fa-lock" style={{ color: '#16a34a', fontSize: 11 }}></i> Items
                    <span style={{ fontSize: 11, background: '#d1fae5', color: '#065f46', borderRadius: 5, padding: '1px 7px', fontWeight: 600 }}>
                      from {sourceQuotations.find(q => String(q.id) === String(sourceQuotId))?.number}
                    </span>
                  </span>
                  <button
                    onClick={() => setSourceQuotId('')}
                    style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 7, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                  >
                    <i className="fas fa-unlock me-1"></i>Edit manually
                  </button>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ padding: '7px 10px', textAlign: 'left',   color: '#64748b', fontSize: 11, fontWeight: 700, borderBottom: '1px solid #e2e8f0' }}>Description</th>
                      <th style={{ padding: '7px 10px', textAlign: 'center', color: '#64748b', fontSize: 11, fontWeight: 700, borderBottom: '1px solid #e2e8f0' }}>Qty</th>
                      <th style={{ padding: '7px 10px', textAlign: 'right',  color: '#64748b', fontSize: 11, fontWeight: 700, borderBottom: '1px solid #e2e8f0' }}>Price</th>
                      <th style={{ padding: '7px 10px', textAlign: 'right',  color: '#64748b', fontSize: 11, fontWeight: 700, borderBottom: '1px solid #e2e8f0' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doc.items.map((item, i) => {
                      const total = (parseFloat(item.qty) || 0) * (parseFloat(item.price) || 0);
                      return (
                        <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                          <td style={{ padding: '8px 10px', color: '#1f2937' }}>{item.description || '—'}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'center', color: '#374151' }}>{item.qty}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: '#374151' }}>{item.price ? fmt(item.price) : '—'}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>{total ? fmt(total) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Advance + totals */}
                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
                  <div style={{ marginBottom: 8 }}>
                    <span style={lbl}>Advance Paid (₹)</span>
                    <input style={inp} type="number" min="0" value={doc.advance} placeholder="0"
                      onChange={e => set('advance', e.target.value)} />
                  </div>
                  {advance > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', background: '#fef9c3', borderRadius: 7, fontSize: 13, marginBottom: 6 }}>
                      <span style={{ color: '#92400e' }}>Total</span>
                      <span style={{ fontWeight: 700, color: '#92400e' }}>{fmt(subtotal)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: '#0a1f5c', borderRadius: 8, color: '#fff' }}>
                    <span style={{ fontWeight: 700 }}>{advance > 0 ? 'Balance' : 'Sub Total'}</span>
                    <span style={{ fontWeight: 800 }}>{fmt(advance > 0 ? balance : subtotal)}</span>
                  </div>
                </div>
              </div>
            ) : (
              /* ── EDITABLE items ── */
              <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                    <i className="fas fa-list me-1" style={{ color: '#2563eb' }}></i> Items
                  </span>
                  <button onClick={addItem}
                    style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 7, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    + Add Row
                  </button>
                </div>

                {doc.items.map((item, i) => {
                  const total = (parseFloat(item.qty) || 0) * (parseFloat(item.price) || 0);
                  return (
                    <div key={item.id} style={{ background: '#f8fafc', borderRadius: 10, padding: 12, marginBottom: 10, border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Item {i + 1}</span>
                        {doc.items.length > 1 && (
                          <button onClick={() => removeItem(item.id)}
                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13 }}>
                            <i className="fas fa-times"></i>
                          </button>
                        )}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <span style={lbl}>Description</span>
                        <input style={inp} value={item.description} placeholder="Item description"
                          onChange={e => setItem(item.id, 'description', e.target.value)} />
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <span style={lbl}>Qty</span>
                          <input style={inp} type="number" min="1" value={item.qty}
                            onChange={e => setItem(item.id, 'qty', e.target.value)} />
                        </div>
                        <div style={{ flex: 2 }}>
                          <span style={lbl}>Price (₹)</span>
                          <input style={inp} type="number" min="0" value={item.price} placeholder="0"
                            onChange={e => setItem(item.id, 'price', e.target.value)} />
                        </div>
                      </div>
                      {total > 0 && (
                        <div style={{ marginTop: 8, textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#16a34a' }}>
                          = {fmt(total)}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Totals summary */}
                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
                  {cfg.showAdvance && (
                    <div style={{ marginBottom: 8 }}>
                      <span style={lbl}>Advance Paid (₹)</span>
                      <input style={inp} type="number" min="0" value={doc.advance} placeholder="0"
                        onChange={e => set('advance', e.target.value)} />
                    </div>
                  )}
                  {cfg.showAdvance && advance > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', background: '#fef9c3', borderRadius: 7, fontSize: 13, marginBottom: 6 }}>
                      <span style={{ color: '#92400e' }}>Total</span>
                      <span style={{ fontWeight: 700, color: '#92400e' }}>{fmt(subtotal)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: '#0a1f5c', borderRadius: 8, color: '#fff' }}>
                    <span style={{ fontWeight: 700 }}>
                      {cfg.showAdvance && advance > 0 ? 'Balance' : 'Sub Total'}
                    </span>
                    <span style={{ fontWeight: 800 }}>
                      {fmt(cfg.showAdvance && advance > 0 ? balance : subtotal)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Advance Calculations (Quotation only — drives the % terms lines) */}
            {cfg.showTerms && (() => {
              const pctNum    = parseFloat(advancePct) || 0;
              const advAmt    = Math.round((pctNum / 100) * subtotal);
              const balAmt    = subtotal - advAmt;
              return (
                <div style={{ ...card, border: '1px solid #bfdbfe', background: '#f0f9ff' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0a1f5c', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className="fas fa-percentage" style={{ color: '#2563eb' }}></i> Advance Calculations
                  </div>

                  {/* % input */}
                  <div style={{ marginBottom: 14 }}>
                    <span style={lbl}>Advance Percentage (%)</span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="number" min="0" max="100" step="1"
                        style={{ ...inp, width: 100, fontWeight: 700, fontSize: 15 }}
                        value={advancePct}
                        onChange={e => applyAdvancePct(e.target.value)}
                      />
                      <span style={{ fontSize: 13, color: '#64748b' }}>% of subtotal</span>
                    </div>
                  </div>

                  {/* Breakdown */}
                  {subtotal > 0 && (
                    <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #bfdbfe', overflow: 'hidden' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 14px', borderBottom: '1px solid #e0e7ff', fontSize: 13 }}>
                        <span style={{ color: '#64748b' }}>Subtotal</span>
                        <span style={{ fontWeight: 700, color: '#0f172a' }}>{fmt(subtotal)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 14px', borderBottom: '1px solid #e0e7ff', fontSize: 13, background: '#fef9c3' }}>
                        <span style={{ color: '#92400e', fontWeight: 600 }}>Advance ({pctNum}%)</span>
                        <span style={{ fontWeight: 700, color: '#d97706' }}>{fmt(advAmt)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 14px', fontSize: 13, background: '#f0fdf4' }}>
                        <span style={{ color: '#166534', fontWeight: 600 }}>Balance</span>
                        <span style={{ fontWeight: 700, color: '#16a34a' }}>{fmt(balAmt)}</span>
                      </div>
                    </div>
                  )}
                  {subtotal === 0 && (
                    <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>Add items above to see the breakdown</p>
                  )}
                </div>
              );
            })()}

            {/* Terms (Quotation only) */}
            {cfg.showTerms && (
              <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                    <i className="fas fa-file-alt me-1" style={{ color: '#2563eb' }}></i> Terms &amp; Conditions
                  </span>
                  <button onClick={addTerm}
                    style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 7, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    + Add
                  </button>
                </div>
                {doc.terms.map((t, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <input style={{ ...inp, flex: 1 }} value={t}
                      onChange={e => setTerm(i, e.target.value)} />
                    <button onClick={() => removeTerm(i)}
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13 }}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* History */}
            <div style={card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>
                <i className="fas fa-history me-1" style={{ color: '#2563eb' }}></i>
                Recent {DOC_CFG[histTab].label}s
              </div>
              {history.length === 0 ? (
                <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>No saved documents yet</p>
              ) : history.slice(0, 10).map(h => (
                <div key={h.id}
                  onClick={() => loadDoc(h)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '9px 12px', borderRadius: 9, marginBottom: 6,
                    background: doc.number === h.number ? '#eff6ff' : '#f8fafc',
                    border: `1px solid ${doc.number === h.number ? '#bfdbfe' : '#e2e8f0'}`,
                    cursor: 'pointer', transition: 'all .15s',
                  }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>{h.number}</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{h.customer_name || '—'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                      {fmt(h.subtotal)}
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{h.date_str}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ══ RIGHT: Live Preview ══ */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0',
              padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,.06)',
              overflow: 'auto', maxHeight: 'calc(100vh - 130px)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: .5 }}>
                  <i className="fas fa-eye me-1"></i> Live Preview
                </span>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{doc.number}</span>
              </div>

              <div style={{ overflowX: 'auto' }}>
                {/* Fixed 794px wrapper gives PrintDoc a concrete width for the preview */}
                <div style={{
                  width: 794,
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                  marginBottom: -Math.round(1123 * (1 - scale)) + 'px',
                }}>
                  <PrintDoc ref={printRef} doc={doc} />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    {/* ── Revision History Modal ── */}
      {showRevHistory && (
        <div
          onClick={() => setShowRevHistory(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 680, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.35)' }}
          >
            <div style={{ padding: '16px 22px', background: '#0a1f5c', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: '#fff', fontWeight: 800, fontSize: 15 }}><i className="fas fa-history me-2"></i>Revision History — {doc.number}</div>
                <div style={{ color: '#93c5fd', fontSize: 12, marginTop: 3 }}>{doc.revision} revision{doc.revision !== 1 ? 's' : ''} · current is the latest</div>
              </div>
              <button onClick={() => setShowRevHistory(false)}
                style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div style={{ overflowY: 'auto', padding: 20, flex: 1 }}>
              {/* Current version */}
              <div style={{ padding: '12px 16px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, color: '#166534', fontSize: 13 }}>
                    <i className="fas fa-check-circle me-1"></i>Current Version (Rev {doc.revision})
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{fmt(subtotal)}</span>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  {doc.items.length} item{doc.items.length !== 1 ? 's' : ''} · {doc.customer.name || '—'}
                </div>
              </div>

              {/* Past revisions newest first */}
              {[...(doc.revision_log || [])].reverse().map((rev, i) => (
                <div key={i} style={{ padding: '12px 16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontWeight: 700, color: '#374151', fontSize: 13 }}>
                      <i className="fas fa-clock me-1" style={{ color: '#94a3b8' }}></i>Rev {rev.revision}
                    </span>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>{fmt(rev.subtotal)}</span>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>
                        {rev.saved_at ? new Date(rev.saved_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </span>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {(rev.items || []).map((item, j) => (
                      <span key={j} style={{ marginRight: 14 }}>
                        {item.description} × {item.qty} @ {fmt(item.price)}
                      </span>
                    ))}
                  </div>
                  {parseFloat(rev.advance) > 0 && (
                    <div style={{ fontSize: 12, color: '#d97706', marginTop: 4 }}>Advance: {fmt(rev.advance)}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
